import React, { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Search } from "lucide-react";
import { roomColor } from "../../FilterBar";
import PopoverPortal from "./PopoverPortal";

/**
 * Compact dropdown showing the current room's abbreviation; opens to pick
 * another. The menu is portaled to <body> so it escapes the schedule grid's
 * sticky stacking contexts.
 *
 * For projects with many rooms (>5), a search input is shown above the list —
 * matches against name OR abbreviation, case-insensitive. Arrow keys navigate;
 * Enter picks the highlighted match; Escape closes.
 */
export default function RoomDropdown({ value, rooms, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const btnRef = useRef(null);
  const inputRef = useRef(null);

  const current = rooms.find((r) => r.id === value);
  const showSearch = rooms.length > 5;

  const filtered = useMemo(() => {
    if (!query.trim()) return rooms;
    const q = query.trim().toLowerCase();
    return rooms.filter((r) =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.abbreviation || "").toLowerCase().includes(q)
    );
  }, [rooms, query]);

  // Reset query + auto-focus the input each time the dropdown opens.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  const pick = (room) => {
    if (!room) return;
    onChange(room.id);
    setOpen(false);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(filtered[active]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className="w-full" onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className={clsx(
          "text-2xs uppercase tracking-wider mono w-full text-left truncate",
          disabled ? "text-ink-mute" : "text-ink hover:text-amber"
        )}
      >
        {current?.abbreviation || current?.name || "—"}
      </button>
      <PopoverPortal anchorRef={btnRef} open={open && !disabled} onClose={() => setOpen(false)}>
        <div className="bg-bg-elevated border border-line-strong shadow-[0_8px_24px_rgba(0,0,0,0.7)] min-w-[220px]">
          {showSearch && (
            <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-line">
              <Search className="w-3 h-3 text-ink-mute shrink-0" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => { setQuery(e.target.value); setActive(0); }}
                onKeyDown={onKey}
                placeholder="Search rooms…"
                className="bg-transparent outline-none text-xs text-ink placeholder:text-ink-mute w-full mono"
                spellCheck={false}
                autoComplete="off"
                data-testid="room-search-input"
              />
            </div>
          )}
          <div className="max-h-[280px] overflow-auto">
            {filtered.length === 0 ? (
              <div className="px-2 py-2 text-2xs text-ink-mute italic">No rooms match.</div>
            ) : (
              filtered.map((r, i) => (
                <button
                  key={r.id}
                  onClick={() => pick(r)}
                  onMouseEnter={() => setActive(i)}
                  className={clsx(
                    "w-full flex items-center gap-2 px-2 py-1 text-xs text-ink",
                    i === active ? "bg-bg-highlight" : "hover:bg-bg-highlight"
                  )}
                  data-testid={`room-option-${r.id}`}
                >
                  <span className="w-1.5 h-1.5 shrink-0" style={{ background: roomColor(r.color_token) }} />
                  <span className="mono uppercase tracking-wider text-2xs">{r.abbreviation}</span>
                  <span className="text-ink-dim truncate">{r.name}</span>
                </button>
              ))
            )}
          </div>
        </div>
      </PopoverPortal>
    </div>
  );
}
